import React from 'react';
import { Card, CardHeader, CardContent, Typography, Box, List, ListItem, ListItemText, Chip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Event as EventIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import type { LifeEvent } from '../../../../types';

interface TimelineHighlightProps {
  events: LifeEvent[];
  totalCount: number;
  personId: string;
  showMetadata?: boolean;
  metadataFormat?: 'short' | 'long';
}

/**
 * TimelineHighlight - Recent 3 timeline events
 */
export const TimelineHighlight: React.FC<TimelineHighlightProps> = ({
  events,
  totalCount,
  personId,
  showMetadata = false,
  metadataFormat = 'short'
}) => {
  const navigate = useNavigate();

  const handleViewAllEvents = () => {
    navigate(`/people/${personId}/timeline`);
  };

  const displayEvents = events.slice(0, 3);

  const getEventTypeColor = (type: string): 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info' | 'default' => {
    const typeMap: Record<string, 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info' | 'default'> = {
      birth: 'success',
      death: 'error',
      marriage: 'primary',
      divorce: 'warning',
      education: 'info',
      career: 'secondary',
      military: 'warning',
      immigration: 'info',
      residence: 'secondary'
    };
    return typeMap[type] || 'default';
  };

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6">Timeline</Typography>
            {totalCount > 0 && (
              <Typography variant="caption" color="text.secondary">
                ({totalCount})
              </Typography>
            )}
          </Box>
        }
        action={
          totalCount > 0 && (
            <Typography
              component="a"
              variant="caption"
              sx={{
                cursor: 'pointer',
                color: 'primary.main',
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' }
              }}
              onClick={handleViewAllEvents}
            >
              View All →
            </Typography>
          )
        }
      />

      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', py: 0 }}>
        {displayEvents.length === 0 ? (
          /* Empty state */
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 4
            }}
          >
            <EventIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center' }}>
              No timeline events yet. Add life events to build their story.
            </Typography>
          </Box>
        ) : (
          /* Events list */
          <List sx={{ py: 0 }}>
            {displayEvents.map((event, index) => (
              <ListItem
                key={event.id}
                divider={index < displayEvents.length - 1}
                sx={{ px: 0 }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Chip
                        label={event.type}
                        size="small"
                        color={getEventTypeColor(event.type)}
                        sx={{ textTransform: 'capitalize' }}
                      />
                      {event.date && (
                        <Typography variant="caption" color="text.secondary">
                          {format(new Date(event.date), 'MMM d, yyyy')}
                        </Typography>
                      )}
                    </Box>
                  }
                  secondary={
                    <>
                      {event.description && (
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          {event.description}
                        </Typography>
                      )}
                      {event.location && (
                        <Typography variant="caption" color="text.secondary">
                          📍 {event.location.city}
                          {event.location.state && `, ${event.location.state}`}
                          {event.location.country && `, ${event.location.country}`}
                        </Typography>
                      )}
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </CardContent>
    </Card>
  );
};
